/**
 * DogAvatar (#545) — a dog's photo thumbnail, or an initial-letter avatar
 * when no photo exists. Most HTHD dogs have no photo (Gingr photos were
 * never imported), so the initial-letter fallback is the COMMON case, not
 * an edge — it must look intentional, never broken.
 *
 * Design philosophy: structural tokens only (no brand color — an avatar
 * placeholder is chrome, not tenant identity); the image carries real alt
 * text; the fallback is aria-hidden because the dog name is always rendered
 * as text right beside it (no information lives only in the avatar).
 */
import { useState } from 'react';

export type DogAvatarSize = 'sm' | 'md';

interface DogAvatarProps {
  name: string;
  photoUrl: string | null;
  size?: DogAvatarSize;
}

function initialOf(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed[0]!.toUpperCase() : '?';
}

export function DogAvatar({
  name,
  photoUrl,
  size = 'md',
}: DogAvatarProps): JSX.Element {
  // A broken/oversized photo URL must not leave a torn image icon on the
  // day-of console — fall back to the initial on load error.
  const [failed, setFailed] = useState(false);
  const showPhoto = Boolean(photoUrl) && !failed;

  if (showPhoto) {
    return (
      <img
        className={`ferro-dog-avatar ferro-dog-avatar--${size} ferro-dog-avatar--photo`}
        src={photoUrl as string}
        alt={name}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      className={`ferro-dog-avatar ferro-dog-avatar--${size} ferro-dog-avatar--initial`}
      aria-hidden="true"
      data-testid="dog-avatar-initial"
    >
      {initialOf(name)}
    </span>
  );
}
